import React, { useEffect, useRef } from "react";
import { Search, Command } from "lucide-react";
import { cn } from "../../lib/utils";
import type { PortalShellCommandPaletteProps } from "../../contracts/portal-shell";

export function PortalShellCommandPalette({
  open,
  query,
  title,
  description,
  inputPlaceholder = "Type a command or search...",
  emptyMessage = "No results found.",
  groups,
  onOpenChange,
  onQueryChange,
  onSelect,
  className,
}: PortalShellCommandPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <div className={cn("fixed inset-0 z-50 flex items-start justify-center pt-[10vh] sm:pt-[15vh]", className)}>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={() => onOpenChange(false)}
      />

      {/* Palette Container */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-panel border border-border bg-card shadow-2xl transition-all">
        <div className="flex items-center border-b border-border px-4 h-14">
          <Search className="mr-2 h-5 w-5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            className="flex h-11 w-full rounded-control bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            placeholder={inputPlaceholder}
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
          {title && (
            <div className="ml-2 hidden items-center gap-1 rounded-control border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground sm:flex">
              <Command className="h-2.5 w-2.5" />
              <span>{title}</span>
            </div>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {description && !query && (
            <div className="px-4 py-3 text-xs text-muted-foreground">
              {description}
            </div>
          )}

          {groups.length === 0 || groups.every(g => g.items.length === 0) ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            groups.map((group) => (
              <div key={group.id} className="mb-2 last:mb-0">
                <h3 className="px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.heading}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        onSelect(item.id);
                        onOpenChange(false);
                      }}
                      disabled={item.disabled}
                      className={cn(
                        "flex w-full items-center rounded-control px-4 py-3 text-sm transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        "focus:bg-accent focus:text-accent-foreground focus:outline-none",
                        item.disabled && "pointer-events-none opacity-50"
                      )}
                    >
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.keywords && item.keywords.length > 0 && (
                         <span className="ml-2 hidden text-[10px] text-muted-foreground sm:inline">
                           {item.keywords.join(", ")}
                         </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex items-center border-t border-border bg-muted/30 px-4 py-3 text-[10px] text-muted-foreground">
          <div className="flex gap-4">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-card px-1.5 py-0.5 font-sans">Enter</kbd>
              <span>to select</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-border bg-card px-1.5 py-0.5 font-sans">Esc</kbd>
              <span>to close</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
